import {
  COMPANY_KEYS,
  matchAmbassadorshipAffectedInfluencers,
  useAmbassadorshipEligibilityQuery,
} from "@/api/companyApi";
import InfoIcon from "@/assets/colorable/info.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import Modal from "@/components/Common/Modal";
import AmbassadorBenefits from "@/components/Common/referral/AmbassadorBenefits";
import type {AmbassadorshipRequestSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {ambassadorRequestModalAtom} from "@/store/jotaiAtoms";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAtom, type ExtractAtomValue} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const MESSAGE_MAX_CHARS = 500;

const AmbassadorRequestModal: FC<{
  data?: ExtractAtomValue<typeof ambassadorRequestModalAtom>;
  onClose?: () => void;
  shouldRenderInline?: boolean;
}> = ({data, onClose, shouldRenderInline}) => {
  const {t} = useTranslation(["influencer_invite", "errors"]);
  const queryClient = useQueryClient();
  const [ambassadorRequestModalData, setAmbassadorRequestModalData] = useAtom(ambassadorRequestModalAtom);
  const requestData = data ?? ambassadorRequestModalData;
  const companyId = requestData?.companyId!;
  const influencer = requestData?.influencer;
  const influencerId = influencer?.uuid!;
  const [message, setMessage] = useState("");

  const {data: eligibility, isLoading: isEligibilityLoading} = useAmbassadorshipEligibilityQuery({
    companyId,
    influencerId,
  });

  const blockedReason =
    eligibility?.blocked_reason === "request_already_sent" &&
    eligibility.last?.kind === "request" &&
    eligibility.last.status === "declined"
      ? "request_declined"
      : eligibility?.blocked_reason;

  const closeModal = onClose ?? (() => setAmbassadorRequestModalData(null));

  const {mutate: sendRequest, isPending} = useMutation({
    meta: {
      successMessage: t("ambassador_request_sent", {name: influencer?.user.first_name}),
    },
    mutationKey: COMPANY_KEYS.mCompanyAmbassadorshipRequestSend,
    mutationFn: () =>
      client.post<AmbassadorshipRequestSerializer>(COMPANY_KEYS.mCompanyAmbassadorshipRequestSend[0], {
        company_profile_id: companyId,
        influencer_id: influencerId,
        ...(message.trim() ? {message: message.trim()} : {}),
      }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: COMPANY_KEYS.qCompanyAmbassadorshipEligibility(companyId, influencerId),
      }),
    onSuccess: ({data: ambassadorshipRequest}) => {
      capturePostHogEvent(PostHogEvents.AmbassadorshipRequestSent, {
        ambassadorshipRequestId: ambassadorshipRequest.id,
        companyId,
        entryPoint: requestData?.entryPoint,
        influencerId,
      });
      queryClient.invalidateQueries(matchAmbassadorshipAffectedInfluencers);
      queryClient.invalidateQueries({
        queryKey: COMPANY_KEYS.qCompanyAmbassadorshipRequests(companyId),
        refetchType: "all",
      });
      closeModal();
    },
  });

  return (
    <Modal
      backgroundClassName={shouldRenderInline ? undefined : "md:w-auto md:max-w-125"}
      confirmButtonText={t("send_invite")}
      isConfirmDisabled={isEligibilityLoading || !eligibility?.can_send}
      isLoading={isPending}
      modalTitle={t("invite_creator_as_ambassador", {name: influencer?.user.first_name})}
      onClose={closeModal}
      onConfirm={sendRequest}
      shouldCloseOnConfirm={false}
      shouldRenderInline={shouldRenderInline}>
      {blockedReason && blockedReason !== "not_worked_with_brand" && (
        <div className="bg-warning-light-yellow flex items-start gap-2 rounded-md p-3">
          <WarningIcon className="text-warning-dark-yellow mt-0.5 size-3.5 shrink-0" />
          <span className="text-neutral-black text-xs">{t(blockedReason, {ns: "errors"})}</span>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <AutosizeTextarea
          className="p-3 text-sm md:placeholder:text-sm"
          maxLength={MESSAGE_MAX_CHARS}
          maxRows={8}
          minRows={4}
          onChange={e => setMessage(e.target.value)}
          optional
          placeholder={t("ambassador_invite_message_placeholder")}
          title={t("ambassador_invite_message_label")}
          value={message}
        />
        <span
          className={clsxm(
            "self-end text-xs",
            message.length >= MESSAGE_MAX_CHARS ? "text-danger-dark-red" : "text-neutral-grey-dark",
          )}>
          {message.length} / {MESSAGE_MAX_CHARS}
        </span>
      </div>
      <AmbassadorBenefits />
      <div className="bg-primary-light-solid flex items-start gap-2 rounded-md p-3">
        <InfoIcon className="text-primary-sky mt-0.5 size-3.5 shrink-0" />
        <span className="text-neutral-black text-xs">{t("ambassador_invite_info")}</span>
      </div>
    </Modal>
  );
};

export default AmbassadorRequestModal;
